import React, { useMemo } from "react";
import {
  FileText, Eye, ScrollText, AlertTriangle,
  Sparkles, Scissors, CalendarHeart, Clock,
  CheckCircle2, XCircle, Wallet,
} from "lucide-react";

/**
 * ClientStatusBar — "vital signs" of a client at a glance.
 *
 * Renders a single horizontal row of soft, rounded capsules — each showing
 * one operational dimension (preventivo · contratto · trial completate ·
 * countdown · warning intelligenti). The goal is to give the operator the
 * answer to "what's the state of this matrimonio?" in under two seconds,
 * without looking like a technical ERP.
 *
 * Self-contained: takes the raw client object and (optionally) the boolean
 * flags `hasMakeup` / `hasHair` derived from the authenticated user's plan.
 * No props soup, no business logic outside this file.
 *
 * Visual contract:
 *   bg-{soft tone}/50  border-{tone}/60  text-{tone strong}
 * Tones: champagne (default/positive), green (done), red (missing/critical),
 * amber (warning), blue (info), neutral (idle).
 */

// Tone palette — soft luxury, no neon. Order matters for the "summary mood":
// when EVERY capsule is green or champagne, the bar feels harmonized.
const TONES = {
  champagne: "bg-[#FAF3E7] border-[#E5D2A8] text-[#7A5C2E]",
  green:     "bg-emerald-50 border-emerald-200 text-emerald-700",
  red:       "bg-rose-50 border-rose-200 text-rose-700",
  amber:     "bg-amber-50 border-amber-200 text-amber-800",
  blue:      "bg-sky-50 border-sky-200 text-sky-700",
  neutral:   "bg-neutral-50 border-neutral-200 text-neutral-600",
};

function diffDays(isoOrYmd) {
  if (!isoOrYmd) return null;
  // Accept both YYYY-MM-DD (wedding_date) and full ISO timestamps (audit log).
  const target = new Date(isoOrYmd.length <= 10 ? `${isoOrYmd}T00:00:00` : isoOrYmd);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t2 = new Date(target);
  t2.setHours(0, 0, 0, 0);
  return Math.round((t2.getTime() - today.getTime()) / 86400000);
}

function relativeIt(days) {
  if (days === null) return "";
  if (days === 0) return "oggi";
  if (days === 1) return "ieri";
  if (days === -1) return "domani"; // we negate elsewhere; never used in past form
  if (days > 0) return `${days} giorni fa`;
  const f = Math.abs(days);
  return `tra ${f} giorni`;
}

function lastOpenedAtFromAudit(client) {
  // The backend pushes one "opened" event the FIRST time the public link
  // is visited. We pick the most recent if there are multiple, to keep the
  // capsule fresh ("Aperto 2 giorni fa" rather than 200).
  const audit = (client?.contract?.audit_log) || [];
  const opens = audit.filter((e) => e?.kind === "opened" && e?.at);
  if (!opens.length) return null;
  opens.sort((a, b) => String(b.at).localeCompare(String(a.at)));
  return opens[0].at;
}

export function ClientStatusBar({ client, hasMakeup = false, hasHair = false }) {
  const capsules = useMemo(() => {
    if (!client) return [];
    const out = [];

    const quote = client.quote || {};
    const contract = client.contract || {};
    const makeupTrial = client.makeup?.trial || {};
    const hairTrial = client.hair?.trial || {};

    // Lead state: keep the bar deliberately silent (only the wedding
    // countdown remains, see below). No need to scream "in bozza" at
    // a brand-new lead — the page header already shows the status.
    const isFreshLead = client.status === "lead";

    // ─── Preventivo ───────────────────────────────────────────────────────
    if (!isFreshLead) {
      if (quote.status === "inviato" || quote.status === "accettato" || quote.status === "rifiutato") {
        const tone = quote.status === "rifiutato" ? "red" : "champagne";
        out.push({
          key: "quote",
          tone,
          icon: quote.status === "rifiutato" ? XCircle : FileText,
          label:
            quote.status === "accettato" ? "Preventivo accettato" :
            quote.status === "rifiutato" ? "Preventivo rifiutato" :
            "Preventivo inviato",
        });

        // ── Apertura del link pubblico (sotto-stato derivato dall'audit log)
        const openedAt = lastOpenedAtFromAudit(client);
        if (openedAt) {
          const days = -1 * (diffDays(openedAt) ?? 0); // openedAt is in the past
          out.push({
            key: "opened",
            tone: "blue",
            icon: Eye,
            label: days <= 0 ? "Aperto oggi" : days === 1 ? "Aperto ieri" : `Aperto ${days} giorni fa`,
          });
        } else if (quote.status === "inviato") {
          // Sent but never opened → soft warning (cipria/amber).
          out.push({
            key: "never-opened",
            tone: "amber",
            icon: AlertTriangle,
            label: "Preventivo mai aperto",
          });
        }
      }
      // bozza state intentionally silent — covered by the page badge.
    }

    // ─── Contratto ────────────────────────────────────────────────────────
    const contractStatus = contract.status || "non_inviato";
    if (!isFreshLead) {
      if (contractStatus === "firmato" || contractStatus === "signed") {
        out.push({
          key: "contract",
          tone: "green",
          icon: CheckCircle2,
          label: "Contratto firmato",
        });
      } else if (contractStatus === "inviato") {
        out.push({
          key: "contract",
          tone: "champagne",
          icon: ScrollText,
          label: "Contratto inviato",
        });
      } else if (quote.status === "inviato" || quote.status === "accettato") {
        // We only nag about a missing contract when the preventivo has gone
        // out — otherwise it's premature.
        out.push({
          key: "contract",
          tone: "red",
          icon: AlertTriangle,
          label: "Contratto non inviato",
        });
      }
    }

    // ─── Trial Makeup / Hair ──────────────────────────────────────────────
    // Show a capsule ONLY when the trial is completed (positive signal).
    // No capsule for "trial da fare": keeps the bar quiet by default and
    // lets the green completion stand out as a milestone.
    if (hasMakeup && makeupTrial.completed) {
      out.push({
        key: "makeup-trial",
        tone: "green",
        icon: Sparkles,
        label: "Trial trucco completata",
      });
    }
    if (hasHair && hairTrial.completed) {
      out.push({
        key: "hair-trial",
        tone: "green",
        icon: Scissors,
        label: "Trial capelli completata",
      });
    }

    // ─── Acconto non inserito (warning silenzioso) ────────────────────────
    // Only relevant once the contract is signed — before that it's noise.
    // No positive "Acconto registrato" capsule on purpose: the bar should
    // surface attention-needed states, not pat the operator on the back.
    if ((contractStatus === "firmato" || contractStatus === "signed")
        && (!quote.deposit || Number(quote.deposit) <= 0)) {
      out.push({
        key: "deposit",
        tone: "amber",
        icon: Wallet,
        label: "Acconto non inserito",
      });
    }

    // ─── Timeline incompleta ──────────────────────────────────────────────
    const blocks = client?.timeline?.blocks || [];
    const hasTimeline = Array.isArray(blocks) && blocks.length > 0;
    // Warn only if wedding is reasonably close — 60 days — and timeline empty.
    const wdDiff = diffDays(client.wedding_date);
    if (!isFreshLead && !hasTimeline && wdDiff !== null && wdDiff >= 0 && wdDiff <= 60) {
      out.push({
        key: "timeline-empty",
        tone: "amber",
        icon: Clock,
        label: "Timeline incompleta",
      });
    }

    // ─── Countdown matrimonio (sempre ultima — è l'informazione più
    // emozionale, vogliamo che resti a destra come "headline") ───────────
    if (wdDiff !== null) {
      let tone = "champagne";
      let label;
      if (wdDiff < 0) {
        tone = "neutral";
        label = `Matrimonio ${Math.abs(wdDiff)} giorni fa`;
      } else if (wdDiff === 0) {
        tone = "green";
        label = "Matrimonio oggi";
      } else if (wdDiff === 1) {
        tone = "green";
        label = "Matrimonio domani";
      } else if (wdDiff <= 7) {
        tone = "red";
        label = `Matrimonio tra ${wdDiff} giorni`;
      } else if (wdDiff <= 30) {
        tone = "amber";
        label = `Matrimonio tra ${wdDiff} giorni`;
      } else {
        tone = "champagne";
        label = `Matrimonio tra ${wdDiff} giorni`;
      }
      out.push({ key: "countdown", tone, icon: CalendarHeart, label, headline: true });
    }

    return out;
  }, [client, hasMakeup, hasHair]);

  if (!capsules.length) return null;

  return (
    <div
      className="mt-5 -mx-1 px-1 flex flex-wrap items-center gap-2 sm:gap-2.5"
      role="status"
      aria-label="Stato operativo del cliente"
      data-testid="client-status-bar"
    >
      {capsules.map((c) => {
        const Icon = c.icon;
        return (
          <span
            key={c.key}
            data-testid={`status-cap-${c.key}`}
            className={[
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs",
              "transition-all duration-200 ease-out",
              "hover:-translate-y-px hover:shadow-[0_2px_10px_rgba(0,0,0,0.04)]",
              c.headline ? "font-medium" : "font-normal",
              TONES[c.tone] || TONES.neutral,
            ].join(" ")}
          >
            <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
            <span className="leading-none whitespace-nowrap">{c.label}</span>
          </span>
        );
      })}
    </div>
  );
}

export default ClientStatusBar;
